import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useParams } from "react-router-dom";
import { Save, Loader2, User, Briefcase, FileText, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import PageHeader from "../components/PageHeader";
import TeamFullProfileTabs from "../components/team/TeamFullProfileTabs";
import StaffPermissionsPanel from "../components/team/StaffPermissionsPanel";

const inputCls = "bg-[#0b0811] border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]";
const labelCls = "text-sm font-medium text-slate-400 mb-1 block";

const SPECIALTY_OPTIONS = [
  { value: "", label: "Aucune" },
  { value: "coach_freelance", label: "Coach" },
  { value: "comptable", label: "Comptable" },
  { value: "developpeur", label: "Developpeur" },
  { value: "marketing", label: "Marketing" },
  { value: "rh", label: "Ressources Humaines" },
  { value: "juridique", label: "Juridique" },
  { value: "formation", label: "Formateur" },
  { value: "autre", label: "Autre" },
];

const CONTRACT_TYPES = [
  { value: "", label: "Non renseigne" },
  { value: "cdi", label: "CDI" },
  { value: "cdd", label: "CDD" },
  { value: "freelance", label: "Freelance" },
  { value: "sous_traitant", label: "Sous-traitant" },
  { value: "stagiaire", label: "Stagiaire" },
  { value: "alternance", label: "Alternance" },
];

export default function StaffProfilePage() {
  const { user, axiosAuth, isAdmin } = useAuth();
  const { userId } = useParams();
  const api = axiosAuth();

  const isOwnProfile = !userId || userId === user?.user_id;
  const targetUserId = userId || user?.user_id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState("personal");
  const [targetUser, setTargetUser] = useState(null);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    mobile: "",
    address: "",
    postal_code: "",
    city: "",
    date_of_birth: "",
    nationality: "",
    specialty: "",
    skills: "",
    certifications: "",
    experience_years: "",
    education: "",
    linkedin: "",
    portfolio: "",
    bio: "",
    contract_type: "",
    contract_start: "",
    contract_end: "",
    hourly_rate: "",
    daily_rate: "",
    siret: "",
    company_name: "",
    vat_number: "",
    bank_iban: "",
    availability: "",
    preferred_schedule: "",
    notes: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        let data;
        if (isOwnProfile) {
          const r = await api.get("/staff/my-profile");
          data = r.data;
        } else {
          const r = await api.get(`/admin/staff/${targetUserId}/profile`);
          data = r.data;
          setTargetUser(data.user || null);
        }
        setForm(prev => {
          const merged = { ...prev };
          for (const k of Object.keys(prev)) {
            if (data[k] !== undefined && data[k] !== null) merged[k] = data[k];
          }
          if (!merged.email && data.email) merged.email = data.email;
          if (!merged.first_name && data.name) {
            const parts = data.name.split(" ");
            merged.first_name = parts[0] || "";
            merged.last_name = parts.slice(1).join(" ") || "";
          }
          return merged;
        });
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [targetUserId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isOwnProfile) {
        await api.put("/staff/my-profile", form);
      } else {
        await api.put(`/admin/staff/${targetUserId}/profile`, form);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  const displayName = isOwnProfile
    ? (user?.name || "Ma Fiche")
    : (targetUser?.name || form.first_name + " " + form.last_name || "Fiche Membre");

  return (
    <div className="p-8 max-w-4xl" data-testid="staff-profile-page">
      <PageHeader
        icon={<User />}
        title={isOwnProfile ? "Ma Fiche" : displayName}
        description={isOwnProfile ? "Remplissez vos informations personnelles et professionnelles" : "Fiche détaillée du membre"}
        breadcrumb={isOwnProfile ? [{ label: "Profil" }] : [{ label: "Admin" }, { label: "Équipe" }, { label: displayName }]}
        testId="staff-profile-header"
        actions={(
          <Button onClick={handleSave} disabled={saving} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="save-profile-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : saved ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {saved ? "Sauvegardé !" : "Sauvegarder"}
          </Button>
        )}
      />

      <div className="space-y-6">
        {/* Fiche complète : Accès / Activité / Sécurité / Performance (sans onglets) */}
        {!isOwnProfile && (
          <TeamFullProfileTabs userId={targetUserId} />
        )}

        {/* Iter 246.8 — Panneau Autorisations (admin Blitzz éditant un staff) */}
        {!isOwnProfile && isAdmin && (
          <StaffPermissionsPanel userId={targetUserId} targetUser={targetUser} />
        )}

        {/* Personal Information */}
        <section data-testid="section-personal">
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 space-y-6">
            <h3 className="text-lg font-bold mb-4">Informations personnelles</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Prenom</label>
                <Input value={form.first_name} onChange={e => set("first_name", e.target.value)} className={inputCls} placeholder="Prenom" data-testid="field-first_name" />
              </div>
              <div>
                <label className={labelCls}>Nom</label>
                <Input value={form.last_name} onChange={e => set("last_name", e.target.value)} className={inputCls} placeholder="Nom" data-testid="field-last_name" />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <Input value={form.email} onChange={e => set("email", e.target.value)} className={inputCls} placeholder="email@exemple.com" type="email" data-testid="field-email" />
              </div>
              <div>
                <label className={labelCls}>Telephone fixe</label>
                <Input value={form.phone} onChange={e => set("phone", e.target.value)} className={inputCls} placeholder="01 23 45 67 89" data-testid="field-phone" />
              </div>
              <div>
                <label className={labelCls}>Mobile</label>
                <Input value={form.mobile} onChange={e => set("mobile", e.target.value)} className={inputCls} placeholder="06 12 34 56 78" data-testid="field-mobile" />
              </div>
              <div>
                <label className={labelCls}>Date de naissance</label>
                <Input type="date" value={form.date_of_birth} onChange={e => set("date_of_birth", e.target.value)} className={inputCls} data-testid="field-dob" />
              </div>
              <div>
                <label className={labelCls}>Nationalite</label>
                <Input value={form.nationality} onChange={e => set("nationality", e.target.value)} className={inputCls} placeholder="Francaise" data-testid="field-nationality" />
              </div>
            </div>
            <div className="pt-2">
              <label className={labelCls}>Adresse</label>
              <Input value={form.address} onChange={e => set("address", e.target.value)} className={inputCls} placeholder="Adresse complete" data-testid="field-address" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Code postal</label>
                <Input value={form.postal_code} onChange={e => set("postal_code", e.target.value)} className={inputCls} placeholder="75001" data-testid="field-postal_code" />
              </div>
              <div>
                <label className={labelCls}>Ville</label>
                <Input value={form.city} onChange={e => set("city", e.target.value)} className={inputCls} placeholder="Paris" data-testid="field-city" />
              </div>
            </div>
          </div>
        </section>

        {/* Professional Information */}
        <section data-testid="section-professional">
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 space-y-6">
            <h3 className="text-lg font-bold mb-4">Informations professionnelles</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Specialite</label>
                <Select value={form.specialty} onValueChange={v => set("specialty", v)}>
                  <SelectTrigger className={inputCls} data-testid="field-specialty">
                    <SelectValue placeholder="Choisir..." />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALTY_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value || "none"}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Annees d'experience</label>
                <Input type="number" value={form.experience_years} onChange={e => set("experience_years", e.target.value)} className={inputCls} placeholder="5" data-testid="field-experience" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Competences (separees par des virgules)</label>
              <Input value={form.skills} onChange={e => set("skills", e.target.value)} className={inputCls} placeholder="Management, Comptabilite, Strategie..." data-testid="field-skills" />
            </div>
            <div>
              <label className={labelCls}>Certifications</label>
              <Input value={form.certifications} onChange={e => set("certifications", e.target.value)} className={inputCls} placeholder="Qualiopi, RNCP..." data-testid="field-certifications" />
            </div>
            <div>
              <label className={labelCls}>Formation / Diplomes</label>
              <Input value={form.education} onChange={e => set("education", e.target.value)} className={inputCls} placeholder="Master en Gestion, MBA..." data-testid="field-education" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>LinkedIn</label>
                <Input value={form.linkedin} onChange={e => set("linkedin", e.target.value)} className={inputCls} placeholder="https://linkedin.com/in/..." data-testid="field-linkedin" />
              </div>
              <div>
                <label className={labelCls}>Portfolio / Site web</label>
                <Input value={form.portfolio} onChange={e => set("portfolio", e.target.value)} className={inputCls} placeholder="https://..." data-testid="field-portfolio" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Bio / Presentation</label>
              <textarea value={form.bio} onChange={e => set("bio", e.target.value)} rows={4}
                className={`w-full rounded-md px-3 py-2 text-sm ${inputCls}`}
                placeholder="Presentez-vous en quelques lignes..." data-testid="field-bio" />
            </div>
          </div>
        </section>

        {/* Contract Information */}
        <section data-testid="section-contract">
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 space-y-6">
            <h3 className="text-lg font-bold mb-4">Informations contractuelles</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Type de contrat</label>
                <Select value={form.contract_type} onValueChange={v => set("contract_type", v)}>
                  <SelectTrigger className={inputCls} data-testid="field-contract_type">
                    <SelectValue placeholder="Choisir..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRACT_TYPES.map(o => (
                      <SelectItem key={o.value} value={o.value || "none"}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Date de debut</label>
                <Input type="date" value={form.contract_start} onChange={e => set("contract_start", e.target.value)} className={inputCls} data-testid="field-contract_start" />
              </div>
              <div>
                <label className={labelCls}>Date de fin (si applicable)</label>
                <Input type="date" value={form.contract_end} onChange={e => set("contract_end", e.target.value)} className={inputCls} data-testid="field-contract_end" />
              </div>
              <div>
                <label className={labelCls}>Disponibilite</label>
                <Input value={form.availability} onChange={e => set("availability", e.target.value)} className={inputCls} placeholder="Temps plein, Mi-temps, 3j/sem..." data-testid="field-availability" />
              </div>
            </div>

            <h4 className="text-base font-semibold text-slate-300 pt-4">Tarification</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Tarif horaire (EUR)</label>
                <Input type="number" value={form.hourly_rate} onChange={e => set("hourly_rate", e.target.value)} className={inputCls} placeholder="80" data-testid="field-hourly_rate" />
              </div>
              <div>
                <label className={labelCls}>Tarif journalier (EUR)</label>
                <Input type="number" value={form.daily_rate} onChange={e => set("daily_rate", e.target.value)} className={inputCls} placeholder="600" data-testid="field-daily_rate" />
              </div>
            </div>

            <h4 className="text-base font-semibold text-slate-300 pt-4">Informations legales (sous-traitants / freelances)</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>SIRET</label>
                <Input value={form.siret} onChange={e => set("siret", e.target.value)} className={inputCls} placeholder="123 456 789 00012" data-testid="field-siret" />
              </div>
              <div>
                <label className={labelCls}>Nom de la societe</label>
                <Input value={form.company_name} onChange={e => set("company_name", e.target.value)} className={inputCls} placeholder="Ma Societe SARL" data-testid="field-company_name" />
              </div>
              <div>
                <label className={labelCls}>Numero TVA</label>
                <Input value={form.vat_number} onChange={e => set("vat_number", e.target.value)} className={inputCls} placeholder="FR12345678901" data-testid="field-vat_number" />
              </div>
              <div>
                <label className={labelCls}>IBAN</label>
                <Input value={form.bank_iban} onChange={e => set("bank_iban", e.target.value)} className={inputCls} placeholder="FR76 1234 5678 9012 3456 7890 123" data-testid="field-iban" />
              </div>
            </div>

            <div>
              <label className={labelCls}>Horaires preferes</label>
              <Input value={form.preferred_schedule} onChange={e => set("preferred_schedule", e.target.value)} className={inputCls} placeholder="Lun-Ven 9h-18h" data-testid="field-schedule" />
            </div>
            <div>
              <label className={labelCls}>Notes internes</label>
              <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={3}
                className={`w-full rounded-md px-3 py-2 text-sm ${inputCls}`}
                placeholder="Notes internes..." data-testid="field-notes" />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
